import React, { useState } from "react";
import styled from "styled-components";
import { useToasts } from "react-toast-notifications";

import Button from "@/atoms/Button";
import { triggerVerificationEmail } from "lib/api/account";
import { formatToast } from "utils";
import { isAPIError } from "lib/api/base";

const Container = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 1.5rem 1rem;
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colors["white"]};
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.13);
  gap: 1.5rem;

  ${({ theme }) => theme.media["lg"]} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;

  ${({ theme }) => theme.media["lg"]} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
  }
`;

const Heading = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.colors["ink-black"]};
  font-size: 1.0625rem;
  font-style: normal;
  font-weight: bold;
`;

const Description = styled.p`
  margin: 0.5rem 0 0;
  color: ${({ theme }) => theme.colors["ink-black"]};
  font-size: 0.875rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 0.5rem;

  ${({ theme }) => theme.media["lg"]} {
    align-items: flex-end;
  }
`;

const StyledButton = styled(Button)`
  min-width: 197px;
`;

const TryAgain = styled.p`
  align-self: center;
  color: ${({ theme }) => theme.colors["warm-gray-2"]};
  font-size: 0.75rem;
  font-weight: 400;
  white-space: pre-line;
  line-height: 135%;
  text-align: center;
`;

export interface CardEmailVerificationProps {
  className?: string;
}

export const CardEmailVerification = ({
  className = "",
}: CardEmailVerificationProps) => {
  const [disabled, setDisabled] = useState(false);
  const [label, setLabel] = useState("Verify My Email");

  const { addToast } = useToasts();

  const handleVerifyEmail = async () => {
    try {
      const res = await triggerVerificationEmail();
      if (isAPIError(res)) {
        if (res.status === 429) {
          addToast(
            ...formatToast("Too many requests. Please try again later.", {
              appearance: "error",
            })
          );
          return;
        }
      }

      setDisabled(true);
      setLabel("Resend");
      addToast(
        ...formatToast("Verification email sent!", { appearance: "success" })
      );
      setTimeout(() => {
        setDisabled(false);
      }, 1000 * 60 * 3); // 3 minutes
    } catch (error) {
      addToast(
        ...formatToast("Failed to trigger verification email.", {
          appearance: "error",
        })
      );
    }
  };

  return (
    <Container className={className}>
      <Content>
        <div>
          <Heading>Email Verification Required</Heading>
          <Description>
            {
              "To maintain access to your account, we’ll need to get you verified.\nClick the button and we’ll send you an email for confirmation."
            }
          </Description>
        </div>
      </Content>
      <ButtonContainer>
        <StyledButton
          onClick={handleVerifyEmail}
          disabled={disabled}
          level="secondary"
        >
          {label}
        </StyledButton>
        {disabled ? (
          <TryAgain>
            {"Please check your email.\nTry again in a moment."}
          </TryAgain>
        ) : null}
      </ButtonContainer>
    </Container>
  );
};

export default CardEmailVerification;
